export const CHECKOUT_URL = "https://orthopedic-en.impultienda.ar/checkout/";

export const images = {
  mockup: "https://cdn.magicpatterns.com/uploads/wZhuhBPuFkJq4DJDg7QzKn/MOCKUP_ORTHOPEDIC-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/dHJ7Ht6nL3UmSm5pnbHwwg/RYAN_CARTER_-_SPINE.png",
};

export const beforeItems = [
  "You spend too much time searching through notes to review orthopedic tests.",
  "You memorize positive and negative findings without a consistent interpretation framework.",
  "You struggle to compare tests that address similar clinical questions.",
  "You feel uncertain when different examination findings appear to conflict.",
  "You find it difficult to organize test findings alongside the patient's history.",
  "You have collected many resources, but the information remains scattered.",
];

export const afterItems = [
  "You have an organized visual reference for selected orthopedic tests.",
  "You follow a consistent framework for reviewing test findings.",
  "You compare tests using structured tables and matrices.",
  "You consider the limitations of individual findings.",
  "You connect examination results with the wider clinical presentation.",
  "You practice your reasoning with structured educational case exercises.",
];

export const situations = [
  {
    icon: "bone",
    title: "You know the tests, but you're not always sure how to interpret the findings.",
    body: "You remember the procedures, but you want a clearer way to understand what a result may suggest and what it cannot establish on its own.",
  },
  {
    icon: "clock",
    title: "You spend too much time reviewing scattered notes.",
    body: "You want to find the relevant information without repeatedly searching through textbooks, saved posts, and disconnected documents.",
  },
  {
    icon: "split",
    title: "You struggle when two tests produce conflicting findings.",
    body: "You need a structured way to compare results, consider limitations, and identify what additional information may be relevant.",
  },
  {
    icon: "book",
    title: "You want more than a list of orthopedic tests.",
    body: "You want comparison tables, interpretation frameworks, and visual maps that help you organize the information.",
  },
  {
    icon: "brain",
    title: "You want to strengthen your clinical reasoning.",
    body: "You want to practice connecting examination findings with patient history, symptoms, and the broader assessment.",
  },
  {
    icon: "run",
    title: "You work with musculoskeletal or sports-related presentations.",
    body: "You need a structured reference for reviewing selected tests used in orthopedic assessment.",
  },
] as const;

export const volumes = [
  {
    number: "I",
    title: "The Orthopedic Test Interpretation Framework",
    lead: "Understand the principles behind interpreting orthopedic test findings.",
    body: "Build a structured approach to reviewing test results, understanding their limitations, and considering how they fit into the patient's overall presentation.",
    items: [
      "The Orthopedic Test Interpretation Framework",
      "Positive and negative findings: interpretation considerations",
      "Sensitivity, specificity, and likelihood ratios",
      "The importance of pre-test probability",
      "Understanding diagnostic uncertainty",
      "Common interpretation mistakes",
      "Questions to consider before drawing conclusions",
      "Clinical context worksheets",
      "A repeatable test interpretation template",
      "Quick-reference reasoning prompts",
    ],
    takeaway: "A structured foundation for reviewing orthopedic test findings without treating a single result as a definitive diagnosis.",
  },
  {
    number: "II",
    title: "The Orthopedic Test Comparison Atlas",
    lead: "Compare selected tests through a consistent visual framework.",
    body: "Instead of reviewing each test in isolation, use structured tables to understand their purpose, findings, limitations, and relevant clinical considerations.",
    items: [
      "Orthopedic test comparison tables",
      "Test purpose and intended clinical question",
      "Positive and negative finding descriptions",
      "Key examination considerations",
      "Interpretation limitations",
      "Comparison worksheets",
      "Test selection considerations",
      "Clinical context prompts",
      "Anatomical region reference tables",
      "Quick-reference comparison sheets",
    ],
    takeaway: "A more organized way to compare selected orthopedic tests and understand the differences between them.",
  },
  {
    number: "III",
    title: "The Shoulder & Upper Extremity Blueprint",
    lead: "Organize your reference material for selected upper-extremity tests.",
    body: "Review selected orthopedic tests used in shoulder, elbow, wrist, and hand assessments through a consistent visual format.",
    items: [
      "Selected shoulder test reference sheets",
      "Rotator cuff test comparisons",
      "Tests associated with shoulder instability assessment",
      "Selected elbow examination references",
      "Wrist and hand test reference sheets",
      "Upper-extremity comparison matrices",
      "Positive and negative finding considerations",
      "Evidence limitations and interpretation cautions",
      "Educational case interpretation exercises",
      "Upper-extremity quick-reference tables",
    ],
    takeaway: "An organized visual reference for studying selected upper-extremity tests and their role in broader assessment.",
  },
  {
    number: "IV",
    title: "The Hip, Knee & Lower Extremity Blueprint",
    lead: "Bring structure to your lower-extremity test references.",
    body: "Review selected tests involving the hip, knee, ankle, and foot through organized comparison tables and interpretation frameworks.",
    items: [
      "Selected hip test references",
      "Knee ligament examination comparisons",
      "Meniscal test reference sheets",
      "Patellofemoral assessment considerations",
      "Ankle and foot test references",
      "Lower-extremity test comparison tables",
      "Findings and limitations reference sheets",
      "Test interpretation worksheets",
      "Clinical context prompts",
      "Educational lower-extremity scenarios",
    ],
    takeaway: "A structured reference for reviewing selected lower-extremity tests and understanding their limitations.",
  },
  {
    number: "V",
    title: "The Spine & Differential Reasoning Blueprint",
    lead: "Understand how spinal test findings fit into the wider clinical picture.",
    body: "Review selected cervical and lumbar examination references alongside neurological assessment considerations and differential reasoning principles.",
    items: [
      "Selected cervical spine test references",
      "Selected lumbar spine test references",
      "Neurological examination considerations",
      "Neurodynamic test interpretation concepts",
      "Screening versus diagnostic considerations",
      "Red-flag and referral consideration prompts",
      "Differential reasoning worksheets",
      "Conflicting-findings scenarios",
      "Clinical context reference tables",
      "Educational spine assessment cases",
    ],
    takeaway: "A structured framework for considering spinal test findings alongside patient history, symptoms, neurological findings, and other relevant information.",
  },
  {
    number: "VI",
    title: "The Clinical Reasoning Casebook",
    lead: "Practice interpreting orthopedic test findings through educational cases.",
    body: "Use structured exercises to review clinical presentations, compare findings, consider limitations, and explain your reasoning.",
    items: [
      "Musculoskeletal educational case scenarios",
      "Cases featuring positive and negative test combinations",
      "Conflicting examination findings",
      "Guided interpretation worksheets",
      "Alternative explanation prompts",
      "Clinical reasoning questions",
      "Suggested reasoning pathways",
      "Self-assessment exercises",
      "Case review templates",
      "Reflection questions for continued learning",
    ],
    takeaway: "An opportunity to practice organizing clinical information and explaining your reasoning through structured educational exercises.",
  },
];

export const tools = [
  { icon: "worksheet", title: "The Test Interpretation Worksheet", body: "A repeatable format for recording the clinical question, relevant findings, limitations, and additional information to consider." },
  { icon: "matrix", title: "The Orthopedic Test Comparison Matrix", body: "A visual table for comparing selected tests by purpose, findings, limitations, and clinical context." },
  { icon: "map", title: "The Test Selection Map", body: "A structured visual aid for organizing examination questions and considering potentially relevant tests." },
  { icon: "chart", title: "The Clinical Findings Matrix", body: "A framework for reviewing test results alongside the patient's history, symptoms, and other examination information." },
  { icon: "search", title: "The Differential Reasoning Worksheet", body: "An educational tool for considering alternative explanations and identifying unanswered questions." },
  { icon: "library", title: "The Anatomical Quick-Reference Library", body: "A collection of selected test references organized by anatomical region." },
  { icon: "brain", title: "The Case Interpretation Template", body: "A guided structure for practicing clinical reasoning with educational cases." },
] as const;

export const bonuses = [
  {
    number: 1,
    title: "Orthopedic Test Interpretation Cards",
    value: 17,
    image: "https://cdn.magicpatterns.com/uploads/13WzwicfZ1xzU17362XHAy/BONSU_1_ORTHOPEDIC-EN.png",
    lead: "Review essential test information in a concise visual format.",
    body: "A collection of reference cards organized around selected orthopedic tests.",
    items: [
      "Test names and intended purpose",
      "Key examination considerations",
      "Positive and negative finding descriptions",
      "Interpretation cautions",
      "Limitations and contextual prompts",
      "Anatomical region labels",
      "Consistent visual reference layouts",
    ],
    why: "Review selected test information in a concise format designed to complement the six main volumes.",
  },
  {
    number: 2,
    title: "The 10-Minute Orthopedic Assessment Review",
    value: 12,
    image: "https://cdn.magicpatterns.com/uploads/fwZaLAqDBmichaiD5vVa4P/BONUS_2_ORTHOPEDIC-EN.png",
    lead: "Organize your thinking with a repeatable review checklist.",
    body: "A structured educational resource to help you review the essential questions surrounding an orthopedic assessment.",
    items: [
      "Clinical question prompts",
      "Relevant history considerations",
      "Examination planning questions",
      "Test interpretation checkpoints",
      "Findings integration worksheet",
      "Documentation prompts",
      "Follow-up questions",
    ],
    why: "Use a consistent structure when studying or reviewing the information involved in an assessment. This is a review tool, not a guarantee that a complete clinical assessment can safely be performed in ten minutes.",
  },
  {
    number: 3,
    title: "The Orthopedic Test Interpretation Mistakes Library",
    value: 17,
    image: "https://cdn.magicpatterns.com/uploads/7QQi32eUktiS8PotJDhUXP/BONUS_3_ORTHOPEDIC-EN.png",
    lead: "Recognize common reasoning pitfalls.",
    body: "A collection of educational examples illustrating frequent mistakes in interpreting orthopedic test findings.",
    items: [
      "Overreliance on isolated positive findings",
      "Misinterpretation of negative results",
      "Confusion between screening and diagnostic purposes",
      "Failure to consider pre-test probability",
      "Ignoring conflicting examination information",
      "Confusing association with confirmation",
      "Overlooking limitations in the available evidence",
      "Guided reflection questions",
    ],
    why: "Develop a more careful, structured approach to reviewing test findings and their limitations.",
  },
  {
    number: 4,
    title: "Clinical Reasoning Case Practice Pack",
    value: 17,
    image: "https://cdn.magicpatterns.com/uploads/dgubEmCYAkiCRjawsa2SS8/BONUS_4_ORTHOPEDIC-EN.png",
    lead: "Practice connecting test findings with the broader clinical presentation.",
    body: "A collection of educational case exercises designed to complement Volume VI.",
    items: [
      "Patient presentation summaries",
      "Relevant examination findings",
      "Selected orthopedic test results",
      "Interpretation questions",
      "Alternative explanation prompts",
      "Suggested reasoning discussions",
      "Self-assessment worksheets",
    ],
    why: "Move beyond reading reference material and practice organizing clinical information.",
  },
  {
    number: 5,
    title: "The 30-Point Orthopedic Assessment Checklist Collection",
    value: 12,
    image: "https://cdn.magicpatterns.com/uploads/aZ8iPWsfzScoeRu2m9xX7C/BONUS_5_ORTHOPEDIC-EN.png",
    lead: "Keep your assessment review structured with practical checklists.",
    body: "A collection of checklists covering different parts of the examination and interpretation process.",
    items: [
      "Defining the clinical question",
      "Reviewing the patient's history",
      "Planning examination components",
      "Considering test limitations",
      "Organizing positive and negative findings",
      "Reviewing neurological information where relevant",
      "Considering alternative explanations",
      "Identifying unanswered questions",
      "Documenting findings",
      "Reviewing the need for additional assessment or referral",
    ],
    why: "Use consistent prompts to support study, documentation, and professional learning.",
  },
];

export const offerItems = [
  { label: "Orthopedic Test Interpretation Blueprint™ — 6 Premium Volumes", value: 67 },
  { label: "Bonus #1 — Orthopedic Test Interpretation Cards", value: 17 },
  { label: "Bonus #2 — The 10-Minute Orthopedic Assessment Review", value: 12 },
  { label: "Bonus #3 — Orthopedic Test Interpretation Mistakes Library", value: 17 },
  { label: "Bonus #4 — Clinical Reasoning Case Practice Pack", value: 17 },
  { label: "Bonus #5 — The 30-Point Orthopedic Assessment Checklist Collection", value: 12 },
];

export const testimonials = [
  {
    name: "Daniel Mitchell",
    role: "Physiotherapist · Musculoskeletal Rehabilitation",
    location: "United States",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/9a2e2ec2-aeb1-40e5-a4e4-149dd9303350.jpg",
    quote: "I used to keep orthopedic test information in several different notebooks and documents. Having the information organized into comparison tables and interpretation frameworks makes it easier for me to review the material and identify the questions I still need to consider.",
  },
  {
    name: "Sofia Bennett",
    role: "Sports Physiotherapist",
    location: "United Kingdom",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/b4d4f12d-55c5-4ba0-840d-1bd22a33a516.jpg",
    quote: "The visual organization is what I appreciate most. Instead of looking at each test as a separate piece of information, I can compare their purpose, findings, and limitations within one consistent format. The case exercises are useful for reviewing my reasoning.",
  },
  {
    name: "Michael Torres",
    role: "Physiotherapist · Orthopedic Rehabilitation",
    location: "Mexico",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/93d73510-4003-450c-a63b-7bf2a053d6a1.jpg",
    quote: "I wanted something more organized than a simple list of special tests. The comparison matrices and worksheets provide a practical structure for studying the tests and reviewing what their findings can and cannot tell us.",
  },
  {
    name: "Emma Richardson",
    role: "Physiotherapist · Musculoskeletal Practice",
    location: "Australia",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/2dd0b1ca-5254-46f3-9c52-86f4696ec803.jpg",
    quote: "The case-based exercises give me a structured way to practice connecting test findings with the rest of the presentation. I particularly like having questions that remind me to consider the limitations of individual results rather than looking at them in isolation.",
  },
  {
    name: "Lucas Ferreira",
    role: "Physiotherapist · Sports Rehabilitation",
    location: "Brazil",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/47b4a5b5-40aa-46af-b2d6-bd72d543105e.jpg",
    quote: "The main advantage for me is having a consistent reference format. The anatomical sections, comparison tables, and worksheets make it easier to organize the material I review during my continuing education.",
  },
  {
    name: "Camila Vargas",
    role: "Physiotherapist · Outpatient Orthopedics",
    location: "Colombia",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/022ce4c7-abbc-4f0b-9e19-c1b0ddc507a5.jpg",
    quote: "What changed for me is how I approach conflicting findings. Instead of trusting one positive test, the worksheets push me to look at pre-test probability and the full history first. It's become my go-to study reference.",
  },
];

export const faqs = [
  { q: "How will I receive the material?", a: "After your payment is confirmed, you'll receive instructions for accessing the digital files." },
  { q: "Is this suitable for physiotherapy students?", a: "The educational frameworks and case exercises may be useful for students learning orthopedic assessment. They should be used alongside formal education, appropriate supervision, and current academic resources." },
  { q: "Is this suitable for experienced physiotherapists?", a: "The Blueprint is designed as an educational reference for physiotherapists who want to organize selected orthopedic test information and review interpretation principles." },
  { q: "Does it cover every orthopedic test?", a: "No. It covers selected tests and anatomical regions within its defined scope. It is not intended to replace comprehensive textbooks or provide an exhaustive encyclopedia of orthopedic tests." },
  { q: "Can this product diagnose musculoskeletal conditions?", a: "No. The Blueprint is an educational reference, not a diagnostic tool. Orthopedic test findings must be interpreted in the context of the patient's history, presentation, and the available evidence." },
  { q: "Do I need to be an orthopedic specialist?", a: "Specialist status is not necessarily required to study the material, but foundational knowledge of musculoskeletal assessment is recommended." },
  { q: "Are the files downloadable?", a: "Yes. The Blueprint is delivered as a downloadable digital collection." },
  { q: "Is it a one-time payment?", a: "Yes. The price is $27, paid once, with no recurring subscription." },
  { q: "What happens if the product doesn't meet my expectations?", a: "Your purchase includes a seven-day satisfaction guarantee. Refer to the published refund policy for the exact conditions and instructions." },
  { q: "Will I receive future updates?", a: "Future updates are not included unless explicitly stated in the final offer." },
];

export const recentBuyers = [
  { name: "María González", country: "México" },
  { name: "Javier Rodríguez", country: "España" },
  { name: "Lucía Fernández", country: "Argentina" },
  { name: "Andrés Martínez", country: "Colombia" },
  { name: "Camila Herrera", country: "Chile" },
  { name: "Diego Ramírez", country: "Perú" },
  { name: "Valeria Castro", country: "Uruguay" },
  { name: "Sebastián Morales", country: "Ecuador" },
  { name: "Daniela Vargas", country: "Costa Rica" },
  { name: "Carlos Mendoza", country: "Guatemala" },
  { name: "Paula Jiménez", country: "Venezuela" },
  { name: "Mateo Rojas", country: "Bolivia" },
  { name: "Sofía Navarro", country: "Paraguay" },
  { name: "Alejandro Ortiz", country: "República Dominicana" },
  { name: "Isabella Reyes", country: "Panamá" },
  { name: "Fernando Silva", country: "Puerto Rico" },
];
